import { useEffect, useState } from 'react'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { FormsElementTypeEnum } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useActivities } from 'modules/core/lib/activities/provider'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { Form, FormEditor, useCreateForm } from 'modules/forms'
import { useLoadForm, useUpdateForm } from 'modules/forms/hooks/useForms'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import UnsavedChangesFormModal from 'modules/forms/components/editor/externalEditor/UnsavedChangesFormModal'
import { FormResourceType } from 'modules/forms/lib/types'

interface FormEditorExternalProps {
  formId: string
  resourceType: FormResourceType
  navigateBack: string
  permittedFieldTypes?: FormsElementTypeEnum[]
  emptyStateText?: {
    title: string
    description: string
    buttonLabel: string
  }
}

const FormEditorExternal = ({
  formId,
  resourceType,
  navigateBack,
  emptyStateText,
  permittedFieldTypes,
}: FormEditorExternalProps) => {
  const navigate = useNavigate()
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState<boolean>(false)
  const { openActivity } = useActivities()

  const [form, setForm] = React.useState<Form | undefined>(undefined)
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false)
  const { access, company } = useCurrentSession()

  //Load the form and update the 'form' state
  const { form: loadedForm, loadingForm } = useLoadForm({
    formId,
    resourceType,
  })
  const { createForm, creatingForm } = useCreateForm()
  const { updateForm, updatingForm } = useUpdateForm()

  useEffect(() => {
    if (loadedForm) {
      setForm(loadedForm)
    }
    // The dependency array should include setForm and setHasUnsavedChanges if they are not guaranteed stable,
    // but in this case, React guarantees their stability, so [loadedForm] is correct.
  }, [loadedForm])

  const handleSave = async () => {
    if (!form) return

    const result = form.id ? await updateForm(form) : await createForm(form, company.id, access.id)
    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: form.id
          ? i18n.t('forms.messages.form_updated')
          : i18n.t('forms.messages.form_created'),
      })
      // Reset unsaved changes after successful save
      setHasUnsavedChanges(false)
    }
    return result
  }

  const handleSaveAndExit = async () => {
    const result = await handleSave()
    if (result?.success) {
      setShowUnsavedChangesModal(false)
      goToPreview()
    }
  }

  const handleExitWithoutSaving = () => {
    setShowUnsavedChangesModal(false)
    goToPreview()
  }

  const handleBackClick = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedChangesModal(true)
    } else {
      goToPreview()
    }
  }

  const onChange = (updatedForm: Form) => {
    setForm(updatedForm)
    setHasUnsavedChanges(true)
  }

  const goToPreview = () => {
    navigate(navigateBack)
  }

  const isLoading = loadingForm || !form

  return (
    <>
      <ResponsivePage.Body
        variant="fullscreen"
        loading={isLoading}
        error={null}
        onActionClick={(key) => {
          switch (key) {
            case 'save':
              handleSave()
              break
            case 'preview':
              openActivity('forms.previewForm', {
                formId: form?.id || formId,
                resourceType: resourceType,
              })
              break
            case 'back':
              handleBackClick()
              break
            default:
              break
          }
        }}
      >
        <Box border={{ top: { style: 'solid', width: 's1', color: 'grey200' } }}>
          <FormEditor
            form={form}
            setForm={onChange}
            isSaving={updatingForm || creatingForm}
            hasUnsavedChanges={hasUnsavedChanges}
            emptyStateText={emptyStateText}
            permittedFieldTypes={permittedFieldTypes}
          />
        </Box>
      </ResponsivePage.Body>
      <UnsavedChangesFormModal
        isOpen={showUnsavedChangesModal}
        onClose={() => setShowUnsavedChangesModal(false)}
        onSaveAndExit={handleSaveAndExit}
        onExitWithoutSaving={handleExitWithoutSaving}
        isSaving={updatingForm || creatingForm}
      />
    </>
  )
}
export default FormEditorExternal
